import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { SERVER_URL } from "../databucket";

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const generateEmailDraftThunk = createAsyncThunk(
  "aiSales/generateEmailDraft",
  async ({ leadContext, emailType, repName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${SERVER_URL}/api/ai/email-draft`,
        { leadContext, emailType, repName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to generate email draft");
    }
  }
);

export const summarizeCallNotesThunk = createAsyncThunk(
  "aiSales/summarizeCallNotes",
  async ({ rawNotes, callData, leadContext }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${SERVER_URL}/api/ai/call-summary`,
        { rawNotes, callData, leadContext },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to summarize call notes");
    }
  }
);

/**
 * Transcribe a RingCentral recording via OpenAI Whisper, then summarise with Claude.
 * Returns: { transcript, structuredSummary, keyFacts, recommendedNextAction,
 *            recommendedFollowupDays, sentiment, callRating, coachingFeedback }
 */
export const transcribeRecordingThunk = createAsyncThunk(
  "aiSales/transcribeRecording",
  async ({ recordingUrl, rcAccessToken, callData, leadContext }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${SERVER_URL}/api/ai/transcribe-recording`,
        { recordingUrl, rcAccessToken, callData, leadContext },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to transcribe recording");
    }
  }
);

/**
 * Get AI-powered follow-up suggestion based on lead's full interaction history.
 * Returns: { suggestedFollowupDate, suggestedFollowupDays, suggestedAction,
 *            actionReason, suggestedEmailType, confidence }
 */
export const getFollowupSuggestionThunk = createAsyncThunk(
  "aiSales/getFollowupSuggestion",
  async ({ leadContext, calls, emails, linkedIn, lastContactedDate, followupDate }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${SERVER_URL}/api/ai/followup-suggestion`,
        { leadContext, calls, emails, linkedIn, lastContactedDate, followupDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Failed to get follow-up suggestion");
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const callSummaryInitial = {
  structuredSummary: "",
  keyFacts: {},
  recommendedNextAction: "",
  recommendedFollowupDays: 7,
  sentiment: "neutral",
  callRating: null,
  coachingFeedback: [],
  transcript: null,
  loading: false,
  error: null,
};

const initialState = {
  emailDraft: {
    subject: "",
    body: "",
    loading: false,
    error: null,
  },
  callSummary: { ...callSummaryInitial },
  followupSuggestion: {
    suggestedFollowupDate: null,
    suggestedFollowupDays: null,
    suggestedAction: null,
    actionReason: null,
    suggestedEmailType: null,
    confidence: null,
    loading: false,
    error: null,
  },
};

const aiSalesSlice = createSlice({
  name: "aiSales",
  initialState,
  reducers: {
    clearEmailDraft(state) {
      state.emailDraft = initialState.emailDraft;
    },
    clearCallSummary(state) {
      state.callSummary = { ...callSummaryInitial };
    },
    clearFollowupSuggestion(state) {
      state.followupSuggestion = initialState.followupSuggestion;
    },
  },
  extraReducers: (builder) => {
    // Email draft
    builder
      .addCase(generateEmailDraftThunk.pending, (state) => {
        state.emailDraft.loading = true;
        state.emailDraft.error = null;
      })
      .addCase(generateEmailDraftThunk.fulfilled, (state, action) => {
        state.emailDraft.loading = false;
        state.emailDraft.subject = action.payload.subject;
        state.emailDraft.body = action.payload.body;
      })
      .addCase(generateEmailDraftThunk.rejected, (state, action) => {
        state.emailDraft.loading = false;
        state.emailDraft.error = action.payload;
      });

    // Call summary (from typed notes)
    builder
      .addCase(summarizeCallNotesThunk.pending, (state) => {
        state.callSummary.loading = true;
        state.callSummary.error = null;
      })
      .addCase(summarizeCallNotesThunk.fulfilled, (state, action) => {
        state.callSummary.loading = false;
        Object.assign(state.callSummary, action.payload);
      })
      .addCase(summarizeCallNotesThunk.rejected, (state, action) => {
        state.callSummary.loading = false;
        state.callSummary.error = action.payload;
      });

    // Follow-up suggestion
    builder
      .addCase(getFollowupSuggestionThunk.pending, (state) => {
        state.followupSuggestion.loading = true;
        state.followupSuggestion.error = null;
      })
      .addCase(getFollowupSuggestionThunk.fulfilled, (state, action) => {
        state.followupSuggestion.loading = false;
        Object.assign(state.followupSuggestion, action.payload);
      })
      .addCase(getFollowupSuggestionThunk.rejected, (state, action) => {
        state.followupSuggestion.loading = false;
        state.followupSuggestion.error = action.payload;
      });

    // Transcribe recording + summarise
    builder
      .addCase(transcribeRecordingThunk.pending, (state) => {
        state.callSummary.loading = true;
        state.callSummary.error = null;
      })
      .addCase(transcribeRecordingThunk.fulfilled, (state, action) => {
        state.callSummary.loading = false;
        Object.assign(state.callSummary, action.payload);
      })
      .addCase(transcribeRecordingThunk.rejected, (state, action) => {
        state.callSummary.loading = false;
        state.callSummary.error = action.payload;
      });
  },
});

export const { clearEmailDraft, clearCallSummary, clearFollowupSuggestion } = aiSalesSlice.actions;
export default aiSalesSlice.reducer;
